<template>
    <div class="container mt-5">
        <div v-if="this.user && this.user.firstName" class="text-center">
            <h1>Welcome Back, {{ this.user.firstName }}!</h1>
        </div>
        <h2 class="text-center">Reset Password</h2>
        <form @submit.prevent="callResetPassword" class="mt-4">
            <!-- New Password Field -->
            <div class="mb-3">
                <label for="newPassword" class="form-label">New Password</label>
                <input type="password" class="form-control" id="newPassword" v-model="newPassword" required
                    minlength="6" />
            </div>

            <!-- Confirm Password Field -->
            <div class="mb-3">
                <label for="confirmPassword" class="form-label">Confirm New Password</label>
                <input type="password" class="form-control" id="confirmPassword" v-model="confirmPassword" required
                    minlength="6" />
            </div>

            <!-- Validation Error -->
            <div v-if="validationError" class="alert alert-warning">
                {{ validationError }}
            </div>

            <!-- API Error Message -->
            <div v-if="error" class="alert alert-danger">
                {{ error }}
            </div>

            <!-- Success Message -->
            <div v-if="success" class="alert alert-success">
                Your password has been successfully reset. You can now <a href="#" @click="$emit('show-login')">log
                    in</a> with your new password.
            </div>

            <!-- Submit Button -->
            <button v-if="!success" type="submit" class="btn btn-primary w-100" :disabled="isLoading">
                <span v-if="isLoading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                <span v-else>Reset Password</span>
            </button>

            <!-- Back to Home Button -->
            <router-link to="/"><button type="button" class="btn btn-link w-100 mt-2">
                    Back to Home
                </button></router-link>
        </form>
    </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
export default {
    name: "ResetPassword",
    computed: {
        ...mapState({
            user: state => state.user,
        }),
    },
    data() {
        return {
            newPassword: "", // User's new password
            confirmPassword: "", // Confirmation of the new password
            isLoading: false, // Loading state for the reset button
            error: null, // Error message from the API
            success: false, // Success state after resetting password
            validationError: null, // Client-side validation errors
            token: "", // Password reset token
        };
    },
    created() {
        // Extract the token from the URL query parameters
        const urlParams = new URLSearchParams(window.location.search);
        this.token = urlParams.get("token") || "";
        const expiration = urlParams.get("expiration") || "";
        if (!this.token) {
            console.log("Invalid or missing password reset token.");
            this.error = "Invalid or missing password reset token.";
        }
        if (this.token && expiration) {
            console.log("Token and expiration", this.token, expiration);
            this.fetchUserData()
            this.login(expiration);
        }
    },
    methods: {
        ...mapActions(['setUser', 'login']),
        validateInputs() {
            this.validationError = null;

            if (this.newPassword !== this.confirmPassword) {
                this.validationError = "Passwords do not match.";
                return false;
            }

            if (this.newPassword.length < 6) {
                this.validationError = "Password must be at least 6 characters long.";
                return false;
            }

            // Add more validation rules as needed (e.g., password strength)

            return true;
        },
        async fetchUserData() {
            try {
                const response = await fetch(this.$API_BASE_URL + '/api/customers/whoami', {
                    method: 'GET',
                    credentials: "include", // Ensures cookies are sent with the request
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    this.firstName = data.firstName;
                    this.lastName = data.lastName;

                    this.setUser(data);
                } else {
                    console.error('Failed to fetch user information');
                }
            } catch (error) {
                console.error('Error:', error);
            }
        },
        async callResetPassword() {
            // Reset previous errors
            this.error = null;
            this.success = false;

            // Validate inputs
            if (!this.validateInputs()) {
                return;
            }

            if (!this.token) {
                this.error = "Cannot reset password without a valid token.";
                return;
            }

            this.isLoading = true;

            try {
                // API Call
                const response = await fetch(`${this.$API_BASE_URL}/api/auth/reset-password`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    credentials: "include", // Ensures cookies are sent with the request
                    body: JSON.stringify({
                        newPassword: this.newPassword,
                    }),
                });

                if (response.ok) {
                    this.success = true;
                    this.newPassword = "";
                    this.confirmPassword = "";
                } else {
                    // Handle errors from the API
                    const errorData = await response.json();
                    this.error = errorData.message || "Failed to reset password. Please try again.";
                }
            } catch (err) {
                // Handle network or unexpected errors
                this.error = "An error occurred. Please check your connection.";
                console.error(err);
            } finally {
                this.isLoading = false;
            }
        },
    },
};
</script>

<style scoped>
/* Add any component-specific styles if needed */
.alert a {
    color: #0d6efd;
    text-decoration: underline;
}

.spinner-border {
    vertical-align: middle;
}
</style>